import { FundsserviceAutocomplete } from '@components/FundsserviceAutocomplete';
import { SectionBackground } from '@components/SectionBackground';
import { useCallback, useEffect, useState } from 'react';
import { useFragment } from 'react-relay';
import { Link, useNavigate } from 'react-router-dom';
import { matchData } from '../../../../utils/filter';
import SectionNavigation from '../../../sectionNavigation';
import { pageSectionClassName } from '../PageSectionsStyles';
import { homeFundServiceSectionFragment } from './HomeFundServiceSection.relay';
import type { AutoCompleteFundNodeType, AutoCompleteFundWithTrancheMergedType, HomeFundServiceSectionPropsType } from './types';

function filterVal(checkVal: any, value: any, index: any, array: any) {
  return matchData(value, checkVal);
}

const getData = (data: readonly AutoCompleteFundNodeType[], limit: number = 0): AutoCompleteFundWithTrancheMergedType[] => {
  const result = [].concat.apply(
    [],
    data
      .filter((fund) => fund.node.largeinvestmentfonds !== 2)
      .map((fund) => {
        return fund.node.tranches.edges.map((tranche) => {
          return {
            fundsname: fund.node.fundsname,
            country: fund.node.country,
            isin: tranche.node.isin,
            url: tranche.node.url,
            salesadmission: tranche.node.salesadmission,
          };
        });
      }),
  );
  return limit > 0 ? result.slice(0, limit) : result;
};

const HomeFundServiceSection: React.FC<HomeFundServiceSectionPropsType> = (props) => {
  const navigate = useNavigate();

  const pageSection = useFragment(homeFundServiceSectionFragment, props.pageSection);

  const filterSalesadmission = useCallback((fund: any, country: any) => {
    if (country) {
      return fund.salesadmission.indexOf(country.toUpperCase()) > -1;
    }
    return true;
  }, []);

  const getInitialCount = useCallback(() => {
    return getData(pageSection.autocomplete.items.edges as any).filter((fund) => filterSalesadmission(fund, props.salesAdmission)).length;
  }, [pageSection.autocomplete.items.edges, props.salesAdmission, filterSalesadmission]);

  const [fundsserviceValue, setFundsserviceValue] = useState('');
  const [resultCount, setResultCount] = useState(getInitialCount);
  const [onlyResultLink, setOnlyResultLink] = useState<string | null>(null);

  useEffect(() => {
    if (pageSection && props.salesAdmission) {
      const newResultCount = getData(pageSection.autocomplete.items.edges as any).filter((fund) => filterSalesadmission(fund, props.salesAdmission)).length;

      setResultCount(newResultCount);
    }
  }, [pageSection, props.salesAdmission, filterSalesadmission]);

  const updateFundsserviceValue = useCallback((value: string, count: number, link: string | null) => {
    setResultCount(count);
    setFundsserviceValue(value);
    setOnlyResultLink(link);
  }, []);

  const getNewLocationDescriptor = useCallback(
    (pathname: string) => ({
      ...props.location,
      pathname: onlyResultLink || pathname,
      state: {
        ...props.location.state,
        fundsList: {
          ...((props.location.state && props.location.state.fundsList) || {}),
          liveFilterInfo: {
            fundsname: [fundsserviceValue],
            isin: [fundsserviceValue],
          },
        },
      },
    }),
    [props.location, onlyResultLink, fundsserviceValue],
  );

  const searchFunds = useCallback(() => {
    const locationDescriptor = getNewLocationDescriptor(pageSection.fundsListUrl);
    navigate({ pathname: locationDescriptor.pathname }, { state: locationDescriptor.state });
  }, [getNewLocationDescriptor, pageSection.fundsListUrl, navigate]);

  const renderToFundsLink = () => {
    let label = pageSection.showAll;
    const count = resultCount;
    if (fundsserviceValue) {
      label = pageSection.show;
    }

    const locationDescriptor = getNewLocationDescriptor(pageSection.fundsListUrl);

    return (
      <Link
        to={locationDescriptor}
        data-home-fundservice-link={true}
        className="mx-2.5 inline-flex items-center justify-center cursor-pointer rounded-[15px] border border-black bg-white px-10 py-1.5 text-xs font-normal font-body uppercase tracking-wide text-accent no-underline outline-none"
      >
        {label} {count !== null ? `( ${count} )` : ''}
      </Link>
    );
  };

  return (
    <div className={pageSectionClassName}>
      <SectionBackground anchorName={pageSection.anchorName} url={process.env.HOST + pageSection.titleImageUrl} backgroundUrl={process.env.HOST + pageSection.titleImageBackgroundUrl} />

      <div className="section-content-wrapper Home-container">
        <div className="text-nav-wrapper">
          <div className="section-text">
            <h1 className="Base-title">{pageSection.title}</h1>
            <p>{pageSection.text}</p>
          </div>
          <SectionNavigation sectionNavigation={props.sectionNavigation} activeIndex={props.index} />
        </div>

        <div className="Home-Fundsservice-form">
          <div className="Home-Fundsservice-input">
            <FundsserviceAutocomplete
              placeholder={pageSection.placeholder}
              value={fundsserviceValue}
              onInput={updateFundsserviceValue}
              fundsListUrl={pageSection.fundsListUrl}
              salesAdmission={props.salesAdmission}
              onSearch={searchFunds}
              autocomplete={pageSection.autocomplete}
              location={props.location}
            />
            {renderToFundsLink()}
          </div>
        </div>
      </div>
    </div>
  );
};

export { HomeFundServiceSection, matchData, filterVal, getData };
export type { AutoCompleteFundNodeType };
